/**
 * Modal para crear cobros masivos a múltiples miembros
 */

import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import { X, Loader2, Receipt, Users, Check, CheckCircle } from "lucide-react";
import { useCreateCharge } from "@/hooks/useFinance";
import { useMembersByClub } from "@/hooks/usePersons";
import { useFinancialConceptsByType } from "@/hooks/useCatalogs";
import { usePermissions } from "@/hooks/usePermissions";
import { formatCurrency } from "@/lib/utils";
import type { FinancialConcept } from "@/lib/services/catalogs";
import { toast } from "react-hot-toast";
import { Timestamp } from "firebase/firestore";

const bulkChargeSchema = z.object({
  conceptId: z.string().min(1, "Selecciona un concepto"),
  description: z.string().optional(),
  amount: z.number().min(1, "El monto debe ser mayor a 0"),
  dueDate: z.string().optional(),
});

type BulkChargeForm = z.infer<typeof bulkChargeSchema>;

interface BulkChargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  periodId: string;
}

export default function BulkChargeModal({
  isOpen,
  onClose,
  clubId,
  periodId,
}: BulkChargeModalProps) {
  const createMutation = useCreateCharge();
  const { data: members, isLoading: loadingMembers } = useMembersByClub(clubId);
  const { data: incomeConcepts } = useFinancialConceptsByType("income");
  const { canSeeMember } = usePermissions();

  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<BulkChargeForm>({
    resolver: zodResolver(bulkChargeSchema),
    defaultValues: {
      amount: 0,
    },
  });

  const watchedAmount = watch("amount");

  // Filtrar miembros activos y por sección del director (si aplica)
  const activeMembers = useMemo(() => {
    return (
      members?.filter(
        (m) =>
          m.status === "active" &&
          canSeeMember({
            clubId: m.clubId,
            section: m.section,
            staffSection: m.staffSection,
          }),
      ) || []
    );
  }, [members, canSeeMember]);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return activeMembers;
    return activeMembers.filter((m) =>
      m.person.fullName?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [activeMembers, searchTerm]);

  // Total a cobrar
  const totalAmount = useMemo(() => {
    return selectedMembers.size * (watchedAmount || 0);
  }, [selectedMembers.size, watchedAmount]);

  const toggleMember = (memberId: string) => {
    const newSet = new Set(selectedMembers);
    if (newSet.has(memberId)) {
      newSet.delete(memberId);
    } else {
      newSet.add(memberId);
    }
    setSelectedMembers(newSet);
  };

  const selectAll = () => {
    setSelectedMembers(new Set(filteredMembers.map((m) => m.id)));
  };

  const deselectAll = () => {
    setSelectedMembers(new Set());
  };

  const onSubmit = async (data: BulkChargeForm) => {
    if (selectedMembers.size === 0) {
      toast.error("Selecciona al menos un miembro");
      return;
    }

    const concept = incomeConcepts?.find(
      (c: FinancialConcept) => c.id === data.conceptId,
    );
    if (!concept) {
      toast.error("Concepto no válido");
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: selectedMembers.size });

    const membersToCharge = activeMembers.filter((m) =>
      selectedMembers.has(m.id),
    );
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < membersToCharge.length; i++) {
      const member = membersToCharge[i];

      try {
        await createMutation.mutateAsync({
          clubId,
          periodId,
          memberId: member.id,
          personId: member.personId || member.id,
          personName: member.person.fullName || "Sin nombre",
          conceptType: concept.category,
          conceptName: concept.name,
          description: data.description || `${concept.name} - Cargo masivo`,
          amount: data.amount,
          currency: "COP",
          amountPaid: 0,
          balance: data.amount,
          status: "pending",
          dueDate: data.dueDate
            ? Timestamp.fromDate(new Date(data.dueDate + "T12:00:00"))
            : undefined,
        });
        successCount++;
      } catch {
        errorCount++;
      }

      setProgress({ current: i + 1, total: membersToCharge.length });
    }

    setIsProcessing(false);

    if (errorCount === 0) {
      toast.success(`${successCount} cobros creados exitosamente`);
      onClose();
    } else {
      toast.error(`${successCount} cobros creados, ${errorCount} errores`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Cobro Masivo</h2>
                <p className="text-sm font-medium text-gray-500">
                  Crear cobros para múltiples miembros
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Configuración del cobro */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Concepto *
                  </label>
                  <Select {...register("conceptId")}>
                    <option value="">Selecciona un concepto</option>
                    {incomeConcepts?.map((concept: FinancialConcept) => (
                      <option key={concept.id} value={concept.id}>
                        {concept.name}
                      </option>
                    ))}
                  </Select>
                  {errors.conceptId && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.conceptId.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto por miembro *
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    {...register("amount", { valueAsNumber: true })}
                  />
                  {errors.amount && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.amount.message}
                    </p>
                  )}
                </div>

                <div>
                  <DatePickerField
                    control={control}
                    name="dueDate"
                    label="Fecha de Vencimiento"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <Input
                    type="text"
                    placeholder="Ej: Cuota Febrero 2024"
                    {...register("description")}
                  />
                </div>
              </div>

              {/* Resumen */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 flex items-center justify-between border-2 border-purple-100 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {selectedMembers.size}
                    </p>
                    <p className="text-xs font-medium text-gray-500">
                      Miembros
                    </p>
                  </div>
                  <X className="w-4 h-4 text-gray-400" />
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {formatCurrency(watchedAmount || 0)}
                    </p>
                    <p className="text-xs font-medium text-gray-500">
                      Por miembro
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-500">
                    Total a cobrar
                  </p>
                  <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                    {formatCurrency(totalAmount)}
                  </p>
                </div>
              </div>

              {/* Selección de miembros */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Seleccionar Miembros ({activeMembers.length} activos)
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Seleccionar todos
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={deselectAll}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Deseleccionar todos
                    </button>
                  </div>
                </div>

                {/* Búsqueda */}
                <Input
                  type="text"
                  placeholder="Buscar miembro..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mb-2"
                />

                {/* Lista de miembros */}
                {loadingMembers ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No hay miembros activos
                  </p>
                ) : (
                  <div className="border-2 border-gray-100 rounded-xl divide-y max-h-60 overflow-y-auto">
                    {filteredMembers.map((member) => (
                      <label
                        key={member.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                      >
                        <input
                          type="checkbox"
                          checked={selectedMembers.has(member.id)}
                          onChange={() => toggleMember(member.id)}
                          className="w-4 h-4 rounded-lg border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                        />
                        <div className="flex-1">
                          <p className="font-medium">
                            {member.person.fullName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {member.position || "Sin cargo"}
                          </p>
                        </div>
                        {selectedMembers.has(member.id) && (
                          <div className="w-6 h-6 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Barra de progreso */}
              {isProcessing && (
                <div className="bg-gray-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      Procesando cobros...
                    </span>
                    <span className="text-sm text-gray-500">
                      {progress.current} / {progress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(progress.current / progress.total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="text-sm text-gray-500">
                {selectedMembers.size > 0 && (
                  <>
                    <Check className="w-4 h-4 inline mr-1" />
                    {selectedMembers.size} miembro(s) seleccionado(s)
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="shadow-sm"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isProcessing || selectedMembers.size === 0}
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-lg shadow-purple-500/25 transition-all duration-300"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Receipt className="w-4 h-4 mr-2" />
                      Crear {selectedMembers.size} Cobros
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
